import React, { createContext, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_PAGINATION } from "../constants/pagination";

type Pagination = {
  current: number;
  pageSize: number;
  total: number;
};

type PageState = {
  searchValue: string;
  pagination: Pagination;
  currentStatus?:string;
  aiLimitation?:boolean;
};

type Store = Record<string, PageState>;

type StoreApi = {
  setValue: (pageKey: string, partialValue: Partial<PageState>) => void;
  getValue: (pageKey: string) => PageState | undefined;
  clearPage: (pageKey: string) => void;
  clearAll: () => void;
};

const PageStoreContext = createContext<StoreApi | null>(null);

const defaultPageState: PageState = {
  searchValue: "",
  pagination: {
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  },
};

export const PageStoreProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [store, setStore] = useState<Store>({});

  const setValue = (pageKey: string, partialValue: Partial<PageState>) => {
    setStore((prev) => ({
      ...prev,
      [pageKey]: {
        ...defaultPageState,
        ...prev[pageKey],
        ...partialValue,
      },
    }));
  };

  const getValue = (pageKey: string) => {
    return store[pageKey];
  };

  const clearPage = (pageKey: string) => {
    setStore((prev) => ({
      ...prev,
      [pageKey]: defaultPageState,
    }));
  };

  const clearAll = () => {
    setStore({});
  };

  return (
    <PageStoreContext.Provider value={{ setValue, getValue, clearPage, clearAll }}>
      {children}
    </PageStoreContext.Provider>
  );
};

export const usePageStoreContext = () => {
  const ctx = useContext(PageStoreContext);
  if (!ctx) throw new Error("usePageStoreContext must be used inside PageStoreProvider");
  return ctx;
};


export const usePageStore = () => {
  const { setValue, getValue, clearPage } = usePageStoreContext();
  const location = useLocation();
  const pageKey = location.pathname.replace("/", "");

  return {
    pageKey,
    setPageValue: (partialValue: Partial<PageState>) =>
      setValue(pageKey, partialValue),
    getPageValue: () => getValue(pageKey) ?? defaultPageState,
    clearPageValue: () => clearPage(pageKey),
  };
};